import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getImovel, getImoveisSimilares } from '../services/api';
import { FaBed, FaBath, FaRulerCombined, FaCar, FaPhone, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { logImovelView } from '../utils/analytics';
import './ImovelDetalhes.css';

const ImovelDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [imovel, setImovel] = useState(null);
  const [similares, setSimilares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    loadImovel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  // Rastrear visualização do imóvel
  useEffect(() => {
    if (imovel) {
      logImovelView(imovel.id, imovel.codigo);
    }
  }, [imovel]);

  const loadImovel = async () => {
    try {
      setLoading(true);
      const response = await getImovel(id);
      setImovel(response.data);
      
      // Carregar imóveis similares (mesmas tags, mesmo tipo ou mesma cidade)
      try {
        const similaresRes = await getImoveisSimilares(id);
        // Garantir que o imóvel atual não apareça na lista (filtro adicional de segurança)
        const similaresFiltrados = (similaresRes.data || []).filter(similar => similar.id !== parseInt(id));
        setSimilares(similaresFiltrados);
      } catch (error) {
        console.error('Erro ao carregar imóveis similares:', error);
        setSimilares([]);
      }
    } catch (error) {
      console.error('Erro ao carregar imóvel:', error);
      navigate('/imoveis');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!imovel) {
    return <div className="error">Imóvel não encontrado</div>;
  }

  return (
    <div className="imovel-detalhes">
      {/* Galeria com Carrossel */}
      <div className="imovel-galeria">
        {imovel.fotos && imovel.fotos.length > 0 ? (
          <>
            <div className="imovel-galeria-main">
              <img 
                src={imovel.fotos[currentPhotoIndex]} 
                alt={`${imovel.descricao} - Foto ${currentPhotoIndex + 1}`}
              />
              {imovel.fotos.length > 1 && (
                <>
                  <button 
                    className="galeria-btn galeria-btn-prev"
                    onClick={() => setCurrentPhotoIndex((prev) => 
                      prev === 0 ? imovel.fotos.length - 1 : prev - 1
                    )}
                    aria-label="Foto anterior"
                  >
                    ‹
                  </button>
                  <button 
                    className="galeria-btn galeria-btn-next"
                    onClick={() => setCurrentPhotoIndex((prev) => 
                      prev === imovel.fotos.length - 1 ? 0 : prev + 1
                    )}
                    aria-label="Próxima foto"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            {imovel.fotos.length > 1 && (
              <div className="imovel-galeria-thumbnails">
                {imovel.fotos.map((foto, index) => (
                  <img
                    key={index}
                    src={foto}
                    alt={`Thumbnail ${index + 1}`}
                    className={currentPhotoIndex === index ? 'active' : ''}
                    onClick={() => setCurrentPhotoIndex(index)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="imovel-placeholder">
            <span>Sem foto disponível</span>
          </div>
        )}
      </div>

      <div className="imovel-content">
        <div className="imovel-main">
          {/* Informações Principais */}
          <div className="imovel-header">
            <div>
              <span className="imovel-codigo">Cód: {imovel.codigo}</span>
              <h1>{imovel.tipo} - {imovel.bairro}</h1>
            </div>
            {imovel.preco && (
              <div className="imovel-preco">
                {imovel.operacao === 'locacao' 
                  ? `${formatPrice(imovel.preco)} / mês`
                  : formatPrice(imovel.preco)
                }
              </div>
            )}
          </div>

          {/* Características */}
          <div className="imovel-caracteristicas">
            <div className="caracteristica-item">
              <FaBed />
              <span>{imovel.quartos} {imovel.quartos === 1 ? 'Quarto' : 'Quartos'}</span>
            </div>
            <div className="caracteristica-item">
              <FaBath />
              <span>
                {(parseInt(imovel.banheiros) || 0) + (parseInt(imovel.lavabos) || 0) + (parseInt(imovel.suites) || 0)} 
                {' '}
                {((parseInt(imovel.banheiros) || 0) + (parseInt(imovel.lavabos) || 0) + (parseInt(imovel.suites) || 0)) === 1 ? 'Banheiro' : 'Banheiros'}
              </span>
            </div>
            <div className="caracteristica-item">
              <FaRulerCombined />
              <span>{imovel.area_construida || imovel.area || 0}m²</span>
            </div>
            {(imovel.garagem || imovel.vagas) > 0 && (
              <div className="caracteristica-item">
                <FaCar />
                <span>{imovel.garagem || imovel.vagas} {(imovel.garagem || imovel.vagas) === 1 ? 'Vaga' : 'Vagas'}</span>
              </div>
            )}
          </div>

          {/* Descrição */}
          <div className="imovel-descricao">
            <h2>Descrição</h2>
            <p>{imovel.descricao || imovel.descricaoDetalhada || 'Sem descrição disponível.'}</p>
          </div>

          {/* Características Adicionais */}
          <div className="imovel-amenidades">
            <h2>Características</h2>
            <div className="amenidades-grid">
              <div className={`amenidade ${imovel.situacao === 'Disponível' ? 'sim' : 'nao'}`}>
                Disponível: {imovel.situacao === 'Disponível' ? 'Sim' : 'Não'}
              </div>
            </div>
          </div>
        </div>

        {/* Informações de Contato */}
        <div className="imovel-sidebar">
          <div className="contact-info">
            <h3>Contato Direto</h3>
            <p><FaPhone /> Ronaldo Gonçalves - (31) 99650-6441</p>
            <p><FaEnvelope /> laremfocoimoveis@gmail.com</p>
          </div>

          <div className="whatsapp-container">
            <a
              href={`https://wa.me/5531996506441?text=${encodeURIComponent(`Gostaria de mais informações sobre o imóvel ${imovel.codigo} - ${imovel.tipo} em ${imovel.bairro}, ${imovel.cidade}.\n\nLink: ${window.location.origin}/imovel/${imovel.id}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <FaWhatsapp /> Informações sobre este imóvel
            </a>
          </div>
        </div>
      </div>

      {/* Imóveis Similares */}
      {similares.length > 0 && (
        <div className="imoveis-similares">
          <h2>Imóveis Similares</h2>
          <div className="similares-grid">
            {similares.map(similar => (
              <div key={similar.id} className="similar-card" onClick={() => navigate(`/imovel/${similar.id}`)}>
                {similar.fotos && similar.fotos.length > 0 && (
                  <img src={similar.fotos[0]} alt={similar.descricao} />
                )}
                <div className="similar-info">
                  <p className="similar-tipo">{similar.tipo}</p>
                  <p className="similar-preco">
                    {similar.operacao === 'locacao' 
                      ? `${formatPrice(similar.preco)} / mês`
                      : formatPrice(similar.preco)
                    }
                  </p>
                  <p className="similar-localizacao">{similar.bairro}, {similar.cidade}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default ImovelDetalhes;

